import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authClient } from '@/lib/auth-client';
import { env } from '@/lib/env';
import {
  recordHttpFailure,
  recordHttpSuccess,
  parseRetryAfter,
} from '@/services/http/circuitBreaker';
import type { UploadProgressCallback } from '@/types';

const DEFAULT_TIMEOUT = 30_000;
const UPLOAD_TIMEOUT = 5 * 60 * 1000; // 5 minutes for file uploads
const TOKEN_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// In-memory token cache to avoid a network call to /api/auth/get-session per request
let cachedToken: string | null = null;
let cacheExpiry = 0;
let pendingSessionRequest: Promise<string | null> | null = null;

export async function getAuthToken(): Promise<string | null> {
  const now = Date.now();
  if (cachedToken && now < cacheExpiry) return cachedToken;

  // Deduplicate concurrent session fetches
  if (pendingSessionRequest) return pendingSessionRequest;

  pendingSessionRequest = authClient
    .getSession()
    .then(session => {
      const token = session?.data?.session?.token ?? null;
      cachedToken = token;
      // Only cache when we have a valid token — a null cache would block
      // re-auth for the entire TTL window if the user logs in later.
      cacheExpiry = token ? Date.now() + TOKEN_CACHE_TTL_MS : 0;
      return cachedToken;
    })
    .catch(() => null)
    .finally(() => {
      pendingSessionRequest = null;
    });

  return pendingSessionRequest;
}

export function clearTokenCache() {
  cachedToken = null;
  cacheExpiry = 0;
}

/** Detect the backend envelope shape { data, meta, timestamp, error? }. */
function isEnvelope(
  body: unknown
): body is { data: unknown; meta: unknown; timestamp: unknown; error?: unknown } {
  return (
    body != null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    'data' in body &&
    'meta' in body &&
    'timestamp' in body
  );
}

function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: env.API_BASE_URL,
    timeout: DEFAULT_TIMEOUT,
    headers: { 'Content-Type': 'application/json' },
    // Cookies for same-origin; Bearer token injected by interceptor for cross-origin
    withCredentials: true,
  });

  // Request interceptor: inject Bearer token
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor: unwrap API envelope + normalize errors
  client.interceptors.response.use(
    response => {
      // A response means the backend is reachable — feed the circuit breaker.
      recordHttpSuccess();
      // Backend wraps responses in { data, meta, timestamp } — unwrap so
      // callers receive the inner payload directly.
      if (isEnvelope(response.data)) {
        response.data = response.data.data;
      }
      return response;
    },
    error => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        // Circuit breaker (LIL-331): count "backend looks down" failures —
        // network errors / timeouts (no response), 5xx, and 429. Any other
        // response (incl. 4xx) proves the backend is up, so reset the counter.
        if (!error.response || (status !== undefined && (status >= 500 || status === 429))) {
          recordHttpFailure(parseRetryAfter(error.response?.headers?.['retry-after']));
        } else {
          recordHttpSuccess();
        }

        if (status === 401) {
          clearTokenCache();
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }

        // Billing 402: the backend returns the standard error envelope. There
        // are two distinct classes (mirrors the 401 → auth:unauthorized
        // convention above):
        //   • NO_ACTIVE_PLAN (route-level ActivePlanGuard, M4) — user has no
        //     active plan; fires on every gated endpoint. Drive the full-page
        //     Paywall via `billing:subscription-required` (SubscriptionGate).
        //   • PLAN_LIMIT_REACHED / FEATURE_NOT_IN_PLAN (EntitlementsService) —
        //     user has a plan but hit a cap / lacks a feature. Drive the
        //     per-feature UpgradeModal via `billing:limit-reached`.
        if (status === 402) {
          const body = error.response?.data;
          const detail =
            isEnvelope(body) && body.error && typeof body.error === 'object'
              ? body.error
              : undefined;
          const code = (detail as { code?: string } | undefined)?.code;
          const eventName =
            code === 'NO_ACTIVE_PLAN' ? 'billing:subscription-required' : 'billing:limit-reached';
          window.dispatchEvent(new CustomEvent(eventName, { detail }));
        }
        // Unwrap envelope on error responses.
        // Error envelopes have { data: null, error: { code, message }, meta, timestamp }.
        // Preserve the `error` field so isApiError() can extract the message.
        const envelope = error.response?.data;
        if (isEnvelope(envelope)) {
          const hasErrorField =
            envelope.error != null &&
            typeof envelope.error === 'object' &&
            'code' in envelope.error;
          return Promise.reject(hasErrorField ? envelope : (envelope.data ?? error));
        }
        return Promise.reject(envelope ?? error);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const client = createHttpClient();

export const httpClient = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await client.get<T>(url, config);
    return res.data;
  },

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await client.post<T>(url, data, config);
    return res.data;
  },

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await client.put<T>(url, data, config);
    return res.data;
  },

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await client.patch<T>(url, data, config);
    return res.data;
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await client.delete<T>(url, config);
    return res.data;
  },

  async uploadFile<T>(
    url: string,
    file: File,
    onProgress?: UploadProgressCallback,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await client.post<T>(url, formData, {
      ...config,
      timeout: UPLOAD_TIMEOUT,
      headers: { ...config?.headers, 'Content-Type': undefined },
      onUploadProgress: onProgress
        ? e => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            onProgress(pct);
          }
        : undefined,
    });
    return res.data;
  },
};
